import * as Sentry from '@sentry/react'
import { useLocation } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { useEffect } from 'react'
import type { Evaluation } from '@harnessio/ff-javascript-client-sdk'

import { useAppDispatch, useAppSelector } from './store/hooks'
import {
  featureFlagsFlagEvaluated,
  selectEmailAddress,
  selectFeatureFlagsEvaluated,
  selectLinkedServices,
  selectRoles,
  selectSessionStatus,
  selectUserID,
  selectUseMaintenanceMode,
  useScribeAccreditationAnalysisChanged,
  useEventsChanged,
  useTranscriptEditorChanged,
  useCreateV4Changed,
  useCreateV5Changed,
  useShowNotesChanged,
  useProfileAssignmentsv4Changed,
  useRecertificationCycleChanged,
  useComplianceChanged,
  useAssignmentAnalyticsChanged,
  useGroupsV2Changed,
  useIBSCV2Changed,
  useMaintenanceModeChanged,
  useTexasJurisprudenceChanged,
  useEventsManagerDashboardChanged,
  useEventsStudentViewChanged,
  useQuizAnalyticsV2Changed,
} from './v3/settings/settings.slice'
import { MaintenancePage } from './v3/components/maintenance/maintenance-page'
import { TourProvider } from './v3/tours/components/tour-provider'
import featureFlagClient, { Event } from './utilities/feature-flag-client'
import useSession from './authentication/hooks/use-session'

const PrivateRoute = ({ element }: any) => {
  let { isAuthenticated, isLoading, loginWithRedirect } = useAuth0()
  let location = useLocation()
  let session = useSession()
  let sessionStatus = useAppSelector(selectSessionStatus)
  let dispatch = useAppDispatch()
  let emailAddress = useAppSelector(selectEmailAddress)
  let userID = useAppSelector(selectUserID)
  let roles = useAppSelector(selectRoles)
  let linkedServiceIDs = useAppSelector(selectLinkedServices)
  let flagsEvaluated = useAppSelector(selectFeatureFlagsEvaluated)
  let useMaintenanceMode = useAppSelector(selectUseMaintenanceMode)

  // Trigger login with redirect when user is not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      // Capture the current path and search params
      let returnTo = location.pathname + location.search

      // Store in sessionStorage as fallback
      sessionStorage.setItem('auth_return_to', returnTo)

      // Trigger Auth0 login with appState containing the return path
      loginWithRedirect({
        appState: { returnTo },
      })
    }
  }, [isAuthenticated, isLoading, location, loginWithRedirect])

  useEffect(() => {
    if (sessionStatus === 'fulfilled') {
      let isProd =
        !(import.meta.env.VITE_FRONTEND_URL || 'test').includes('test') &&
        !import.meta.env.VITE_FRONTEND_URL.includes('localhost')

      if (isProd) {
        Sentry.setUser({
          id: userID,
          email: emailAddress,
        })
      }

      try {
        // Register all Feature Flags in redux store
        let client = featureFlagClient({
          identifier: emailAddress.replace('+', ''),
          attributes: {
            roles,
            linkedServiceIDs,
          },
        })

        client.on(Event.FLAGS_LOADED, () => {
          let useScribeAccreditationAnalysis = client.variation(
            'useScribeAccreditationAnalysis',
            false,
          ) as boolean

          dispatch(
            useScribeAccreditationAnalysisChanged(
              useScribeAccreditationAnalysis,
            ),
          )

          let useEvents = client.variation('useEvents', false) as boolean

          dispatch(useEventsChanged(useEvents))

          let enableTransciptEditor = client.variation(
            'enableTranscriptEditor',
            false,
          ) as boolean

          dispatch(useTranscriptEditorChanged(enableTransciptEditor))

          let useShowNotes = client.variation('useShowNotes', false) as boolean

          dispatch(useShowNotesChanged(useShowNotes))

          let useCreateV4 = client.variation('useCreateV4', false) as boolean

          dispatch(useCreateV4Changed(useCreateV4))

          let useCreateV5 = client.variation('useCreateV5', false) as boolean

          dispatch(useCreateV5Changed(useCreateV5))

          let useProfileAssignmentsv4 = client.variation(
            'useProfileAssignmentsv4',
            false,
          ) as boolean

          dispatch(useProfileAssignmentsv4Changed(useProfileAssignmentsv4))

          let useRecertificationCycle = client.variation(
            'useRecertificationCycle',
            false,
          ) as boolean

          dispatch(useRecertificationCycleChanged(useRecertificationCycle))

          let useCompliance = client.variation(
            'useCompliance',
            false,
          ) as boolean

          dispatch(useComplianceChanged(useCompliance))

          let useAssignmentAnalytics = client.variation(
            'useAssignmnentAnalytics',
            false,
          ) as boolean

          dispatch(useAssignmentAnalyticsChanged(useAssignmentAnalytics))

          let useGroupsV2 = client.variation('useGroupsV2', false) as boolean

          dispatch(useGroupsV2Changed(useGroupsV2))

          let useIBSCV2 = client.variation('useISBCV2', false) as boolean

          dispatch(useIBSCV2Changed(useIBSCV2))

          let maintenanceModeFlag = client.variation(
            'useMaintenanceMode',
            false,
          ) as boolean

          dispatch(useMaintenanceModeChanged(maintenanceModeFlag))

          let useTexasJurisprudence = client.variation(
            'useTexasJurisprudence',
            false,
          ) as boolean

          dispatch(useTexasJurisprudenceChanged(useTexasJurisprudence))

          let useEventsManagerDashboard = client.variation(
            'useEventsManagerDashboard',
            false,
          ) as boolean

          dispatch(useEventsManagerDashboardChanged(useEventsManagerDashboard))

          let useEventsStudentView = client.variation(
            'useEventsStudentView',
            false,
          ) as boolean

          dispatch(useEventsStudentViewChanged(useEventsStudentView))

          let useQuizAnalyticsV2 = client.variation(
            'useQuizAnalyticsV2',
            false,
          ) as boolean

          dispatch(useQuizAnalyticsV2Changed(useQuizAnalyticsV2))

          // TODO: We probably should probably handle a failed call to
          // Harness and dispatch this action to set the flags as evaluated
          dispatch(featureFlagsFlagEvaluated())
        })

        // FLAGS_LOADED fires from cache (localStorage) before the network
        // response arrives. Any flag that didn't exist in the cache at the
        // time FLAGS_LOADED fired will return its default value. CHANGED fires
        // when the network sync resolves a flag to a different value, so we
        // need this to pick up newly-created flags on first load.
        client.on(Event.CHANGED, (flagInfo: Evaluation) => {
          if (flagInfo.flag === 'useAssignmnentAnalytics') {
            dispatch(useAssignmentAnalyticsChanged(flagInfo.value as boolean))
          }
          if (flagInfo.flag === 'useGroupsV2') {
            dispatch(useGroupsV2Changed(flagInfo.value as boolean))
          }
          if (flagInfo.flag === 'useISBCV2') {
            dispatch(useIBSCV2Changed(flagInfo.value as boolean))
          }
          if (flagInfo.flag === 'useMaintenanceMode') {
            dispatch(useMaintenanceModeChanged(flagInfo.value as boolean))
          }
          if (flagInfo.flag === 'useTexasJurisprudence') {
            dispatch(useTexasJurisprudenceChanged(flagInfo.value as boolean))
          }
          if (flagInfo.flag === 'useEventsManagerDashboard') {
            dispatch(
              useEventsManagerDashboardChanged(flagInfo.value as boolean),
            )
          }
          if (flagInfo.flag === 'useEventsStudentView') {
            dispatch(useEventsStudentViewChanged(flagInfo.value as boolean))
          }
          if (flagInfo.flag === 'useQuizAnalyticsV2') {
            dispatch(useQuizAnalyticsV2Changed(flagInfo.value as boolean))
          }
        })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error initializing feature flags:', error)
        dispatch(featureFlagsFlagEvaluated())
      }
    }
  }, [sessionStatus, session])

  return isLoading && !flagsEvaluated ? (
    <></>
  ) : isAuthenticated ? (
    flagsEvaluated ? (
      useMaintenanceMode ? (
        <MaintenancePage />
      ) : (
        <TourProvider>{element}</TourProvider>
      )
    ) : (
      <></>
    ) // Do not render `element` until flags are evaluated, React runs the hooks if this ternary is only one level deep
  ) : (
    <></> // Redirecting to login via useEffect
  )
}

export default PrivateRoute
